import { ArrowUpRight, Bug, CirclePause, Cloud, CloudOff, LoaderCircle, Server, Download, Ellipsis, Hand, Keyboard, MessagesSquare, Pin, PinOff, Plus, PlugZap, Power, Search, Settings, Trash2, Wallet } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { AssistantSettings, Conversation, ConversationSummary } from '@orgus/protocol';
import {
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  IconButton,
  Input,
  LiveBadge,
  OrgusMark,
  Pressable,
  Skeleton,
  SkeletonRows,
  StatusPill,
  formatAbsolute,
  formatInt,
  formatMoney,
  hasMod,
  useNow,
} from '@orgus/ui';
import type { LiveMode, PillStatus } from '@orgus/ui';
import { PageActions } from '../components/shell/TopBar.tsx';
import { useCan } from '../features/auth/useMe.ts';
import { AutonomyMenu } from '../features/assistant/AutonomyMenu.tsx';
import { Composer } from '../features/assistant/Composer.tsx';
import { draftIds, readDraft, useQueue, useQueued, writeDraft } from '../features/assistant/composerState.ts';
import { ConversationList } from '../features/assistant/ConversationList.tsx';
import { deleteConversation, patchConversation, sendMessage, startConversation, stopReply, useAssistant, useConversation, useConversationCache, useLive } from '../features/assistant/data.ts';
import { PIN_LIMIT, transcriptFileName, transcriptMarkdown } from '../features/assistant/listing.ts';
import { CONVERSATION_STATUS, EXAMPLE_PROMPTS, isBusy, pickConversation, sortConversations } from '../features/assistant/model.ts';
import { FindBar, QuestionRail, ShortcutsDialog } from '../features/assistant/Navigator.tsx';
import { JumpToLatest, Thread } from '../features/assistant/Thread.tsx';
import { buildTurns } from '../features/assistant/turns.ts';
import { useConnection, useMachines } from '../lib/store.ts';
import { ModuleAbsent } from '../features/server/ModuleAbsent.tsx';
import { useCloud } from '../features/cloud/data.ts';
import { demoRunner, runnerLine, runnerStatus } from '../features/assistant/runner.ts';

const PILL: Record<Conversation['state'], PillStatus> = { idle: 'idle', running: 'working', waiting: 'needs-you', failed: 'failed' };

function useLiveMode(): LiveMode {
  const { connection, mode } = useConnection();
  if (mode === 'mock' || connection === 'live') return 'live';
  if (connection === 'reconnecting' || connection === 'connecting') return 'reconnecting';
  return 'offline';
}

function AssistantSkeleton() {
  return (
    <div className="flex h-full min-h-0" role="status" aria-label="Loading the assistant">
      <div className="m-2 mr-0 hidden w-72 shrink-0 rounded-overlay bg-surface lg:block">
        <SkeletonRows rows={6} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-4 p-6">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="ml-auto h-10 w-2/3 rounded-card" />
        <Skeleton className="h-24 w-full rounded-card" />
      </div>
    </div>
  );
}

/** "build-01 · Opus" for the header: where the next reply runs and on what. */
function useWhere(c: Pick<Conversation, 'machineId' | 'model'> | null, settings: AssistantSettings, model: string | null) {
  const machines = useMachines();
  const id = c ? c.machineId : settings.machineId;
  // Unpinned, the header names the machine Orgus would pick now, the same one the strip over the composer names.
  const picked = id ? null : runnerStatus(machines, null);
  const machine = id ? (machines.find((m) => m.id === id)?.name ?? 'an offline machine') : picked?.kind === 'ready' ? picked.machine : 'a machine Orgus picks';
  const chosen = model ?? c?.model ?? settings.model;
  return { machine, model: chosen ? chosen : 'its default model' };
}

/** Save a text file in the browser. */
function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TitleEditor({ title, onSave, onCancel }: { title: string; onSave: (t: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(title);
  const done = useRef(false);
  const finish = (save: boolean) => {
    if (done.current) return;
    done.current = true;
    if (save && v.trim() && v.trim() !== title) onSave(v.trim());
    else onCancel();
  };
  return (
    <Input
      aria-label="Conversation name"
      autoFocus
      value={v}
      maxLength={120}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish(true);
        if (e.key === 'Escape') finish(false);
      }}
      wrapperClassName="min-w-0 flex-1 basis-24 max-sm:order-last max-sm:basis-full"
    />
  );
}

function ConversationHeader({
  conversation,
  summary,
  settings,
  conversations,
  onOpen,
  menu,
  onRename,
}: {
  conversation: Conversation | null;
  summary: ConversationSummary | null;
  settings: AssistantSettings;
  conversations: ConversationSummary[];
  onOpen: (id: string) => void;
  menu: React.ReactNode;
  onRename: (t: string) => void;
}) {
  const head = conversation ?? summary;
  const lastModel = conversation?.replies?.findLast((r) => r.model)?.model ?? null;
  const where = useWhere(head, settings, lastModel);
  const look = head ? CONVERSATION_STATUS[head.state] : null;
  const [renaming, setRenaming] = useState(false);
  return (
    <header className="flex shrink-0 flex-col gap-1 px-8 pb-2 pt-4 max-md:px-4">
      <div className="flex min-h-control min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <div className="lg:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton label="Conversations" icon={<MessagesSquare />} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuItem icon={<Plus />} onSelect={() => onOpen('new')}>
                New conversation
              </DropdownMenuItem>
              {conversations.map((c) => (
                <DropdownMenuItem key={c.id} onSelect={() => onOpen(c.id)}>
                  {c.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {renaming && head ? (
          <TitleEditor
            title={head.title}
            onCancel={() => setRenaming(false)}
            onSave={(t) => {
              setRenaming(false);
              onRename(t);
            }}
          />
        ) : head ? (
          <Pressable onClick={() => setRenaming(true)} className="min-w-0 flex-1 basis-24 rounded-control px-1 text-left text-lead text-fg hover:bg-hover max-sm:order-last max-sm:line-clamp-2 max-sm:basis-full sm:truncate" aria-label={`${head.title}, rename this conversation`}>
            {head.title}
          </Pressable>
        ) : (
          <h2 className="min-w-0 flex-1 basis-24 truncate text-lead text-fg max-sm:order-last max-sm:basis-full">New conversation</h2>
        )}
        {/* The status wraps with its controls, never stranded beside the title. On a phone the
            controls share the first row with the switcher and the title takes its own row. */}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {head && look && <StatusPill status={PILL[head.state]} label={look.label} className="mr-2 hidden shrink-0 sm:inline-flex" />}
          <AutonomyMenu mode={settings.mode} />
          {head && menu}
        </span>
      </div>
      <p className="flex min-w-0 items-center gap-x-3 gap-y-1 px-1 text-caption text-fg-subtle sm:flex-wrap">
        <span className="min-w-0 truncate">
          Runs on {where.machine} with {where.model}
        </span>
        {head && head.turns > 0 && (
          <span className="shrink-0 whitespace-nowrap num">
            {formatMoney(head.cost)} over {formatInt(head.turns)} {head.turns === 1 ? 'turn' : 'turns'}
          </span>
        )}
        {head && head.by !== 'you' && head.by !== 'owner' && <span className="hidden sm:inline">Started by {head.by}</span>}
      </p>
    </header>
  );
}

function greeting(now: number): string {
  const h = new Date(now).getHours();
  return h >= 5 && h < 12 ? 'Good morning' : h >= 12 && h < 18 ? 'Good afternoon' : 'Good evening';
}

const PROMPT_ICON = [Hand, CirclePause, Wallet, Bug] as const;

/** A new conversation: the mark, a greeting and four ways to start, each filling the box (never sending). */
function Opening({ onPick, now }: { onPick: (text: string) => void; now: number }) {
  return (
    <div className="mx-auto flex w-full max-w-(--layout-width-prose) flex-col items-center gap-8 py-16 text-center">
      <OrgusMark size={40} decorative className="motion-arrive" />
      <div className="motion-arrive flex flex-col gap-2">
        <h3 className="text-heading font-semibold text-fg">{greeting(now)}</h3>
        <p className="text-body text-fg-muted">Ask about your sessions, pipelines, machines and spend.</p>
      </div>
      <ul className="grid w-full grid-cols-1 gap-3 text-left sm:grid-cols-2" aria-label="Ways to start">
        {EXAMPLE_PROMPTS.map((p, i) => {
          const Icon = PROMPT_ICON[i % PROMPT_ICON.length]!;
          return (
            <li key={p} className="motion-arrive">
              <Pressable onClick={() => onPick(p)} className="group/prompt flex h-full items-start gap-3 rounded-card border border-border bg-surface px-4 py-3 text-ui text-fg-muted shadow-elevation-flat hover:border-border-strong hover:bg-hover hover:text-fg">
                <span className="flex h-5 shrink-0 items-center" aria-hidden>
                  <Icon className="size-4 text-fg-subtle group-hover/prompt:text-fg-muted" />
                </span>
                <span className="min-w-0 flex-1">{p}</span>
                <ArrowUpRight className="size-4 shrink-0 self-center text-fg-faint transition-transform duration-(--duration-fast) group-hover/prompt:-translate-y-px group-hover/prompt:translate-x-px group-hover/prompt:text-fg-muted" aria-hidden />
              </Pressable>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Assistant: talk to the native AI agent that reads and runs Orgus, within the autonomy you set. */

/** Where the next reply runs, said before anyone types; with nowhere to run it, the one step to take. */
function RunnerStrip({ settings, machineId }: { settings: AssistantSettings; /** The open conversation's machine: its replies stay there, as its header says. */ machineId?: string | null }) {
  const machines = useMachines();
  const cloud = useCloud();
  const navigate = useNavigate();
  const s = demoRunner(cloud.mock) ?? runnerStatus(machines, cloud.state, machineId ?? settings.machineId);
  const icon = s.kind === 'ready' ? <Server className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : s.kind === 'starting' ? <LoaderCircle className="size-4 shrink-0 animate-spin text-status-running motion-reduce:animate-none" aria-hidden /> : s.kind === 'wake' ? <Cloud className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : <CloudOff className="size-4 shrink-0 text-status-attention" aria-hidden />;
  return (
    <div role="status" aria-label="Where replies run" className="mb-2 flex flex-wrap items-center gap-2 text-caption text-fg-muted">
      <span className="flex min-w-0 flex-1 basis-full items-center gap-2 sm:basis-0">
        {icon}
        <span className="min-w-0 flex-1">{runnerLine(s)}</span>
      </span>
      {s.kind === 'none' && (
        <>
          <Button variant="secondary" size="sm" icon={<Server />} onClick={() => navigate('/machines?add=computer')}>
            Add a machine
          </Button>
          {cloud.canCreate && (
            <Button variant="ghost" size="sm" icon={<Cloud />} onClick={() => navigate('/machines?add=cloud')}>
              Create a cloud VM
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default function Assistant() {
  const view = useAssistant();
  const live = useLiveMode();
  const navigate = useNavigate();
  const canConfigure = useCan('admin');
  const [params, setParams] = useSearchParams();
  const conversations = useMemo(() => sortConversations(view.state?.conversations ?? []), [view.state?.conversations]);
  const selectedId = pickConversation(params.get('c'), conversations);
  const conv = useConversation(selectedId);
  const liveText = useLive(selectedId);
  const summary = conversations.find((c) => c.id === selectedId) ?? null;
  const [draft, setDraftState] = useState(() => readDraft(selectedId));
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [findOpen, setFindOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [stuck, setStuck] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Set<string>>(() => draftIds());
  const composer = useRef<HTMLTextAreaElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const queue = useQueued(selectedId);
  const now = useNow(60_000);

  // Each conversation keeps its own draft.
  useEffect(() => setDraftState(readDraft(selectedId)), [selectedId]);
  const setDraft = useCallback(
    (v: string) => {
      setDraftState(v);
      writeDraft(selectedId, v);
      setDrafts(draftIds());
    },
    [selectedId],
  );

  const open = useCallback(
    (id: string) => {
      setParams(id === 'new' ? { c: 'new' } : { c: id });
      setFindOpen(false);
      requestAnimationFrame(() => composer.current?.focus());
    },
    [setParams],
  );

  const conversation = conv.conversation;
  const busy = conversation ? isBusy(conversation.state) : false;
  const turns = useMemo(() => (conversation ? buildTurns(conversation) : []), [conversation]);
  const lastMessage = useMemo(() => [...turns].reverse().find((t) => t.user)?.user?.text ?? null, [turns]);

  // Follow the newest, as a chat does, unless you scrolled up to read.
  const stuckRef = useRef(true);
  useEffect(() => {
    stuckRef.current = true;
    setStuck(true);
  }, [selectedId]);
  const toBottom = useCallback((smooth = false) => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);
  useEffect(() => {
    if (stuckRef.current) toBottom();
  }, [selectedId, conversation?.entries.length, conversation?.state, liveText?.text, toBottom]);
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const onScroll = () => {
      const at = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      stuckRef.current = at;
      setStuck(at);
    };
    const ro = new ResizeObserver(() => {
      if (stuckRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', onScroll);
    };
  }, [view.phase, selectedId]);

  const send = useCallback(
    async (text: string) => {
      stuckRef.current = true;
      if (selectedId) await sendMessage(selectedId, text);
      else {
        const c = await startConversation(text);
        writeDraft(null, '');
        setParams({ c: c.id });
      }
      setDraft('');
    },
    [selectedId, setParams, setDraft],
  );

  // A message held while the reply ran goes as soon as the reply is done.
  useEffect(() => {
    if (!selectedId || busy || !queue.length || !conversation) return;
    const next = useQueue.getState().shift(selectedId);
    if (next) void sendMessage(selectedId, next.text).catch(() => useQueue.getState().add(selectedId, next.text));
  }, [selectedId, busy, queue.length, conversation]);

  const report = (e: unknown, what: string) => setActionError(`${what} ${e instanceof Error ? e.message : ''}`.trim());
  const rename = async (id: string, title: string) => {
    try {
      await patchConversation(id, { title });
    } catch (e) {
      report(e, 'The name was not changed.');
    }
  };
  const pin = async (id: string, pinned: boolean) => {
    try {
      await patchConversation(id, { pinned });
    } catch (e) {
      report(e, 'The conversation was not pinned.');
    }
  };
  const downloadTranscript = async (id: string) => {
    const c = useConversationCache.getState().byId[id];
    if (!c) return setActionError('Open the conversation once, then download it.');
    download(transcriptFileName(c), transcriptMarkdown(c, formatAbsolute));
  };

  // Keyboard: Ctrl+K search, Ctrl+Shift+O new, Ctrl+F find, Ctrl+/ shortcuts, / to write.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.closest('input, textarea, [contenteditable="true"]') !== null);
      if (hasMod(e) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        search.current?.focus();
      } else if (hasMod(e) && e.shiftKey && e.key.toLowerCase() === 'o') {
        e.preventDefault();
        open('new');
      } else if (hasMod(e) && e.key.toLowerCase() === 'f' && selectedId) {
        e.preventDefault();
        setFindOpen(true);
      } else if (hasMod(e) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
      } else if (e.key === '/' && !typing && !hasMod(e)) {
        e.preventDefault();
        composer.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selectedId]);

  const actions = (
    <PageActions>
      {/* The primary action first: the top bar drops what does not fit, last first. Ctrl+Shift+O stays bound (shortcuts sheet). */}
      <Button variant="secondary" size="sm" icon={<Plus />} onClick={() => open('new')} aria-keyshortcuts="Control+Shift+O Meta+Shift+O">
        New conversation
      </Button>
      {canConfigure && <IconButton label="Assistant settings" icon={<Settings />} onClick={() => navigate('/settings/assistant')} />}
    </PageActions>
  );

  if (view.phase === 'loading') {
    return (
      <>
        {actions}
        <AssistantSkeleton />
      </>
    );
  }
  if (view.phase === 'missing') {
    return (
      <ModuleAbsent
        size="page"
        icon={<PlugZap />}
        feature="The assistant"
        hub={{ title: 'The assistant is not available on this hub', description: 'Update Orgus on the hub machine and restart it.' }}
        instead="Start sessions from Sessions and answer approvals and questions from the Inbox."
      />
    );
  }
  if (view.phase === 'error' || !view.state) {
    return (
      <div className="p-6">
        <ErrorState error={view.error} onRetry={view.retry} />
      </div>
    );
  }

  const settings = view.state.settings;
  const head = conversation ?? summary;
  const pinFull = conversations.filter((c) => c.pinned).length >= PIN_LIMIT;
  const blocked = settings.enabled
    ? null
    : canConfigure
      ? 'The assistant is off. Turn it on in Settings, Assistant.'
      : 'The assistant is off. An owner or admin can turn it on in Settings, Assistant.';

  const menu = head && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton label="Conversation actions" icon={<Ellipsis />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuItem icon={<Search />} shortcut={['mod', 'f']} onSelect={() => setFindOpen(true)}>
          Find in this conversation
        </DropdownMenuItem>
        {head.pinned ? (
          <DropdownMenuItem icon={<PinOff />} onSelect={() => void pin(head.id, false)}>
            Unpin
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem icon={<Pin />} disabled={pinFull} onSelect={() => void pin(head.id, true)}>
            {pinFull ? `Pin (up to ${PIN_LIMIT})` : 'Pin'}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem icon={<Download />} disabled={!conversation?.entries.length} onSelect={() => void downloadTranscript(head.id)}>
          Download transcript
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Keyboard />} shortcut={['mod', '/']} onSelect={() => setShortcutsOpen(true)}>
          Keyboard shortcuts
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Trash2 />} variant="destructive" disabled={busy} onSelect={() => setConfirmDelete(head.id)}>
          Delete conversation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const deleting = confirmDelete ? (conversations.find((c) => c.id === confirmDelete) ?? null) : null;

  return (
    <div className="flex h-full min-h-0">
      {actions}
      <aside className="m-2 mr-0 hidden w-72 shrink-0 flex-col rounded-overlay border border-border bg-surface shadow-elevation-flat lg:flex" aria-label="Conversations">
        <div className="flex h-12 shrink-0 items-center gap-2 pl-4 pr-2">
          <h2 className="text-ui font-medium text-fg">Conversations</h2>
          {conversations.length > 0 && <span className="text-caption text-fg-subtle num">{formatInt(conversations.length)}</span>}
          <LiveBadge mode={live} className="ml-auto" />
          {/* Always reachable here: a narrow top bar hides its page actions. */}
          <IconButton size="xs" label="New conversation" icon={<Plus />} shortcut={['mod', 'shift', 'o']} onClick={() => open('new')} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          <ConversationList
            conversations={conversations}
            selected={selectedId}
            drafts={drafts}
            searchRef={search}
            now={now}
            actions={{ onOpen: open, onRename: rename, onPin: pin, onDownload: (id) => void downloadTranscript(id), onDelete: (id) => setConfirmDelete(id) }}
          />
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col" aria-label={head?.title ?? 'New conversation'}>
        <ConversationHeader conversation={conversation} summary={summary} settings={settings} conversations={conversations} onOpen={open} menu={menu} onRename={(t) => head && void rename(head.id, t)} />
        {findOpen && conversation && <FindBar turns={turns} scroller={scroller} onClose={() => setFindOpen(false)} />}
        <div className="relative min-h-0 flex-1">
          <div ref={scroller} className="h-full overflow-y-auto">
            <div className="mx-auto flex w-full max-w-(--layout-width-prose) flex-col gap-4 px-8 pb-8 pt-4 max-md:px-4">
              {live === 'offline' && (
                <div role="status" className="flex items-center gap-3 rounded-card border border-border bg-surface px-3 py-2 text-ui text-fg-muted">
                  <CloudOff className="size-4 shrink-0 text-status-attention" aria-hidden />
                  Lost the live connection to the hub. Showing what it last sent; new replies show once it reconnects.
                </div>
              )}
              {!settings.enabled && (
                <div role="status" className="flex items-center gap-3 rounded-card border border-border bg-surface px-3 py-2 text-ui text-fg-muted">
                  <Power className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                  <span className="min-w-0 flex-1">The assistant is off, so it answers nothing. Past conversations stay readable.</span>
                  {canConfigure && (
                    <Link to="/settings/assistant" className="shrink-0 text-ui font-medium text-accent-text underline-offset-2 hover:underline">
                      Open assistant settings
                    </Link>
                  )}
                </div>
              )}
              {actionError && (
                <div role="alert" className="flex items-center gap-3 rounded-card border border-status-danger-line bg-status-danger-soft px-3 py-2 text-ui text-fg">
                  <span className="min-w-0 flex-1">{actionError}</span>
                  <Button variant="ghost" size="sm" onClick={() => setActionError(null)}>
                    Dismiss
                  </Button>
                </div>
              )}
              {!selectedId ? (
                <Opening
                  now={now}
                  onPick={(p) => {
                    setDraft(p);
                    requestAnimationFrame(() => composer.current?.focus());
                  }}
                />
              ) : conv.phase === 'error' && !conversation ? (
                <ErrorState
                  error={conv.error}
                  onRetry={conv.retry}
                  action={
                    <Button variant="ghost" onClick={() => open('new')}>
                      Start a new conversation
                    </Button>
                  }
                />
              ) : !conversation ? (
                <div className="flex flex-col gap-4" role="status" aria-label="Loading the conversation">
                  <Skeleton className="ml-auto h-10 w-2/3 rounded-card" />
                  <Skeleton className="h-24 w-full rounded-card" />
                </div>
              ) : (
                <>
                  {conv.phase === 'error' && <ErrorState variant="inline" error={conv.error} onRetry={conv.retry} />}
                  <Thread
                    conversation={conversation}
                    live={liveText}
                    now={now}
                    actions={{
                      onResend: (text) => void send(text).catch((e) => report(e, 'The message was not sent.')),
                      onEdit: (text) => {
                        setDraft(text);
                        requestAnimationFrame(() => composer.current?.focus());
                      },
                    }}
                  />
                </>
              )}
            </div>
          </div>
          {conversation && <QuestionRail turns={turns} scroller={scroller} />}
          {!stuck && conversation && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
              <JumpToLatest unread={busy} onJump={() => toBottom(true)} />
            </div>
          )}
        </div>
        <div className="shrink-0 px-8 pb-4 pt-2 max-md:px-4">
          <div className="mx-auto w-full max-w-(--layout-width-prose)">
            {settings.enabled && <RunnerStrip settings={settings} machineId={(conversation ?? summary)?.machineId} />}
            <Composer
              ref={composer}
              value={draft}
              onChange={setDraft}
              onSend={send}
              busy={busy}
              onStop={selectedId ? async () => void (await stopReply(selectedId)) : undefined}
              onQueue={selectedId ? (text) => useQueue.getState().add(selectedId, text) : undefined}
              queued={queue}
              onEditQueued={(id, text) => selectedId && useQueue.getState().edit(selectedId, id, text)}
              onRemoveQueued={(id) => selectedId && useQueue.getState().remove(selectedId, id)}
              lastMessage={lastMessage}
              blocked={blocked}
            />
          </div>
        </div>
      </section>

      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Delete conversation "${deleting?.title ?? 'this conversation'}"?`}
        description="Its messages and steps are removed from this hub. The audit log keeps every step it ran."
        confirmLabel="Delete conversation"
        tone="danger"
        onConfirm={async () => {
          const id = confirmDelete!;
          await deleteConversation(id);
          writeDraft(id, '');
          setConfirmDelete(null);
          if (id === selectedId) setParams({});
        }}
      />
    </div>
  );
}

